import React, { useState, Dispatch, SetStateAction, useEffect } from 'react';
import { Typography, Button, Box, TextField, Modal } from '@mui/material';
import RegisterButton from './registerButton';

import { REGEX } from '../../../constant';

const ModalWrapper = React.forwardRef<
  typeof Box,
  { children: React.ReactNode }
>(({ children }, ref) => {
  return (
    <Box
      ref={ref}
      tabIndex={-1}
      flexDirection={'column'}
      rowGap={3}
      sx={{
        display: 'flex',
        position: 'absolute',
        top: '50%',
        left: '50%',
        transform: 'translate(-50%, -50%)',
        minWidth: '500px',
        bgcolor: 'background.paper',
        border: 2,
        borderColor: 'black',
        borderRadius: '3px',
      }}
    >
      {children}
    </Box>
  );
});

const ModalHeader = () => {
  return (
    <Box
      display={'flex'}
      minHeight={'50px'}
      height={'10%'}
      bgcolor={'#007DC5'}
      color={'white'}
      alignItems={'center'}
    >
      <Typography variant='h5' marginLeft={2}>
        器械登録
      </Typography>
    </Box>
  );
};

const SerialForm = (props: {
  serial: string;
  setSerial: Dispatch<SetStateAction<string>>;
  isSerialValid: boolean;
  setIsSerialValid: Dispatch<SetStateAction<boolean>>;
}) => {
  const { serial, setSerial, isSerialValid, setIsSerialValid } = props;

  const handleSerialChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    const newSerial = e.target.value;
    setSerial(newSerial);

    // If new serial is empty, do not make an error
    if (!newSerial) {
      setIsSerialValid(true);
      return;
    }

    setIsSerialValid(RegExp(REGEX.SERIAL).exec(newSerial) !== null);
  };

  return (
    <Box display={'flex'} flexDirection={'column'} alignItems={'flex-start'}>
      <TextField
        label='シリアル'
        helperText={!isSerialValid ? 'シリアルの形式が正しくありません' : ''}
        error={!isSerialValid}
        variant='outlined'
        type='text'
        value={serial}
        onChange={handleSerialChange}
        required
        inputProps={{
          maxLength: 9,
        }}
      />
    </Box>
  );
};

const CodeForm = (props: {
  code: string;
  setCode: Dispatch<SetStateAction<string>>;
  isCodeValid: boolean;
  setIsCodeValid: Dispatch<SetStateAction<boolean>>;
}) => {
  const { code, setCode, isCodeValid, setIsCodeValid } = props;

  const handleCodeChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    const newCode = e.target.value;
    setCode(newCode);

    // If new code is empty, do not make an error
    if (!newCode) {
      setIsCodeValid(true);
      return;
    }

    setIsCodeValid(RegExp(REGEX.CODE).exec(newCode) !== null);
  };

  return (
    <Box display={'flex'} flexDirection={'column'} alignItems={'flex-start'}>
      <TextField
        label='IDコード'
        helperText={!isCodeValid ? 'コードの形式が正しくありません' : ''}
        error={!isCodeValid}
        variant='outlined'
        type='text'
        value={code}
        onChange={handleCodeChange}
        required
        inputProps={{
          maxLength: 4,
        }}
      />
    </Box>
  );
};

const DealerForm = (props: {
  dealer: string;
  setDealer: Dispatch<SetStateAction<string>>;
  isDealerValid: boolean;
  setIsDealerValid: Dispatch<SetStateAction<boolean>>;
}) => {
  const { dealer, setDealer, isDealerValid, setIsDealerValid } = props;

  const handleDealerChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    const newDealer = e.target.value;
    setDealer(newDealer);

    // If new code is empty, do not make an error
    if (!newDealer) {
      setIsDealerValid(true);
      return;
    }

    setIsDealerValid(RegExp(REGEX.DEALER).exec(newDealer) !== null);
  };

  return (
    <>
      <Box display={'flex'} flexDirection={'column'} alignItems={'flex-start'}>
        <TextField
          label='販売店名'
          helperText={!isDealerValid ? '販売店名の形式が正しくありません' : ''}
          error={!isDealerValid}
          variant='outlined'
          type='text'
          value={dealer}
          onChange={handleDealerChange}
          required
          inputProps={{
            maxLength: 80,
          }}
        />
      </Box>
      <Typography variant='body2' color={'red'}>
        {'※販売店名は後で変更できます'}
      </Typography>
    </>
  );
};

const RegisterModal = (props: {
  isRegisterModalOpen: boolean;
  setIsRegisterModalOpen: Dispatch<SetStateAction<boolean>>;
}) => {
  const { isRegisterModalOpen, setIsRegisterModalOpen } = props;

  const [serial, setSerial] = useState('');
  const [code, setCode] = useState('');
  const [dealer, setDealer] = useState('');
  const [isSerialValid, setIsSerialValid] = useState(true);
  const [isCodeValid, setIsCodeValid] = useState(true);
  const [isDealerValid, setIsDealerValid] = useState(true);

  useEffect(() => {
    setDealer(localStorage.getItem('latestInputDealer') ?? '');
  }, [isRegisterModalOpen]);

  return (
    <Modal
      open={isRegisterModalOpen}
      onClose={() => {
        setIsRegisterModalOpen(!isRegisterModalOpen);
      }}
      aria-labelledby='modal-modal-title'
      aria-describedby='modal-modal-description'
    >
      <ModalWrapper>
        <ModalHeader />
        <Box
          display={'flex'}
          alignItems={'center'}
          justifyContent={'center'}
          flexDirection={'column'}
          columnGap={15}
        >
          <Box marginBottom={4}>
            <SerialForm
              serial={serial}
              setSerial={setSerial}
              isSerialValid={isSerialValid}
              setIsSerialValid={setIsSerialValid}
            />
          </Box>
          <Box marginBottom={4}>
            <CodeForm
              code={code}
              setCode={setCode}
              isCodeValid={isCodeValid}
              setIsCodeValid={setIsCodeValid}
            />
          </Box>
          <Box marginBottom={4}>
            <DealerForm
              dealer={dealer}
              setDealer={setDealer}
              isDealerValid={isDealerValid}
              setIsDealerValid={setIsDealerValid}
            />
          </Box>
          <Box
            display={'flex'}
            flexDirection={'column'}
            alignItems={'flex-start'}
            marginBottom={1}
          >
            <Typography variant='body1' color={'red'}>
              ・保証開始日は器械の初回登録日です
            </Typography>
            <Typography variant='body1' color={'red'}>
              ・保証期間は保証開始日から1年です
            </Typography>
          </Box>
        </Box>
        <Box
          display={'flex'}
          flexDirection={'row'}
          justifyContent={'center'}
          columnGap={5}
          marginBottom={5}
        >
          <RegisterButton
            serial={serial}
            isSerialValid={isSerialValid}
            code={code}
            isCodeValid={isCodeValid}
            dealer={dealer}
            isDealerValid={isDealerValid}
            setIsRegisterModalOpen={setIsRegisterModalOpen}
          />
          <Button
            variant='contained'
            color={'inherit'}
            sx={{ width: '150px' }}
            onClick={() => {
              setIsRegisterModalOpen(false);
            }}
          >
            {'キャンセル'}
          </Button>
        </Box>
      </ModalWrapper>
    </Modal>
  );
};

export default RegisterModal;
